// ─── External libs ────────────────────────────────────────────────────────────
import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import ReactGA from "react-ga4";
import { HelmetProvider, Helmet } from "react-helmet-async";

// ─── Styles ───────────────────────────────────────────────────────────────────
import "react-image-lightbox/style.css";
import "rsuite/dist/rsuite.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import GlobalStyles from "./styles/GlobalStyles";
import { light } from "./styles/Themes";

// ─── Layout shells ────────────────────────────────────────────────────────────
import Navigation from "./components/sections/HeaderFooter/Navigation";
import Footer from "./components/sections/HeaderFooter/Footer";
import ScrollToTop from "./components/sections/HeaderFooter/ScrollToTop";
import PageTitle from "./components/common/PageTitle";
import Breadcrumbs from "./components/common/Breadcrumbs/Breadcrumbs";

// ─── Page components ──────────────────────────────────────────────────────────
import Home from "./components/sections/Home";
import About from "./components/sections/About/About";
import Works from "./components/sections/Works/Works";
import Need from "./components/sections/Need/Need";
import Rogyog from "./components/sections/RogYog/Rogyog";
import Yog from "./components/sections/Yog/Yog";
import SliderComponent from "./components/sections/Slider/SliderComponent";
import Testimonials from "./components/sections/Testimonials/Testimonials";
import Packages from "./components/sections/Packages/Packages";
import GalleryPage from "./components/sections/Gallery/GalleryPage";
import Blog from "./components/sections/Blog/Blog";
import BlogDescription from "./components/sections/Blog/BlogDescription";
import ContactUsForm from "./components/sections/contactUs/ContactUsForm";
import ContactUSPrenatal from "./components/sections/contactUs/ContactUSPrenatal";
import Diet from "./components/sections/contactUs/Diet";
import PrivacyPolicy from "./components/sections/PrivacyPolicy/PrivacyPolicy";
import CookiePolicy from "./components/sections/CookiePolicy/CookiePolicy";
import TermsAndConditions from "./components/sections/TermsAndConditions/Terms";
import DietPlanner from "./components/sections/Diet/Dietplanner";
import YogaGame from "./components/sections/YogaGame/YogaGame";
import AdvertisementForm from "./components/sections/contactUs/AdvertisementForm";
import YogGoals from "./components/sections/YogGoals/YogGoals";
import DietPlannerWizard from "./components/sections/WelcomeScreen/Welcome/DietPlannerWizard";
import YogGoalAsanas from "./components/sections/YogGoals/Category/YogGoalAsanas";
import Availability from "./components/sections/Availability/Availability";

// ═══════════════════════════════════════════════════════════════════════════════
// ANALYTICS — initialise once at module level, skip on localhost/dev
// ═══════════════════════════════════════════════════════════════════════════════

const GA_TRACKING_ID = "G-9QH6R7RSL6";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  window.location.hostname !== "localhost";

if (IS_PRODUCTION) {
  ReactGA.initialize(GA_TRACKING_ID);
}

// ═══════════════════════════════════════════════════════════════════════════════
// SITE-WIDE STRUCTURED DATA — Organisation + WebSite schema
// Injected once at the app root so every page benefits.
// Update SITE_URL and the logo path before deploying.
// ═══════════════════════════════════════════════════════════════════════════════

const SITE_URL = "https://www.yoga-shastra.com";

const organisationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Yoga Shastra",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  sameAs: [
    "https://www.instagram.com/yogashastra",
    "https://www.facebook.com/yogashastra",
    "https://www.youtube.com/@yogashastra",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    areaServed: "IN",
    availableLanguage: ["English", "Hindi"],
  },
};

const webSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Yoga Shastra",
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/online-yoga?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// LAYOUT WRAPPER
//
// PageLayout provides the common site shell.
//
// breadcrumbs:
//   Optional array.
//   If omitted, no breadcrumb is rendered.
//
// Example:
//   breadcrumbs={[
//     { label: "Home", path: "/" },
//     { label: "Packages" },
//   ]}
// ═══════════════════════════════════════════════════════════════════════════════

const PageLayout = ({
  seo,
  breadcrumbs,
  children,
}) => (
  <>
    <Navigation />

    {/* position:relative so the breadcrumb can be absolutely
        positioned over the top of whatever section renders first
        (hero, gradient, etc.) instead of pushing it down as its
        own solid-colour strip. */}
    <div style={{ position: "relative" }}>
      {breadcrumbs?.length > 0 && (
        <Breadcrumbs items={breadcrumbs} />
      )}

      {children}
    </div>

    <Footer />

    {/* Rendered LAST on purpose: with react-helmet-async the most recently
        mounted tag wins, so the route's SEO must come after every section
        so that a section can't override the page title/description. */}
    {seo}
  </>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SEO CONFIGS — one source of truth for every route's <PageTitle> props
// ═══════════════════════════════════════════════════════════════════════════════

const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const SEO = {
  home: {
    title: "Online Prenatal & Postnatal Yoga Classes | Yoga Shastra",
    description:
      "Live online prenatal and postnatal yoga classes with certified instructors. Ease pregnancy discomfort, build strength, and recover safely after birth.",
    path: "/",
    keywords:
      "online prenatal yoga classes, postnatal yoga online, pregnancy yoga at home, online yoga classes India, yoga for normal delivery, postpartum yoga recovery, live yoga for pregnant women, certified prenatal yoga instructor, yoga shastra",
    ogImage: OG_IMAGE,
  },

  yogaForHealth: {
    title: "Disease-Specific Yoga Therapy Online | Yoga Shastra",
    description:
      "Targeted online yoga therapy for chronic conditions — diabetes, thyroid, back pain, and more — with personalised asana plans from certified instructors.",
    path: "/yoga-for-health",
    keywords:
      "yoga therapy online, yoga for diabetes, yoga for thyroid, yoga for back pain, yoga for PCOS, yoga for high blood pressure, yoga for chronic conditions, therapeutic yoga classes, yoga asanas for diseases",
    ogImage: OG_IMAGE,
  },

  onlineYoga: {
    title: "Online Yoga Classes for Every Style | Yoga Shastra",
    description:
      "Live online yoga sessions for every level — immunity-boosting practice, stress relief, and classic asanas guided by expert instructors from anywhere.",
    path: "/online-yoga",
    keywords:
      "online yoga classes, live yoga classes online, online yoga classes India, yoga for beginners online, hatha yoga online, yoga for immunity, yoga for stress relief, yoga asanas for beginners, yoga at home with instructor",
    ogImage: OG_IMAGE,
  },

  packages: {
    title: "Yoga Class Packages & Free Demo | Yoga Shastra",
    description:
      "Flexible online yoga packages for prenatal, postnatal, and wellness classes. Book a free demo class and find the plan that fits your schedule.",
    path: "/packages",
    keywords:
      "online yoga class packages, yoga class price India, free yoga demo class, prenatal yoga package, postnatal yoga package, online yoga subscription plans, book yoga trial class",
    ogImage: OG_IMAGE,
  },

  about: {
    title: "About Yoga Shastra | Our Instructors & Mission",
    description:
      "Meet the certified Yogacharyas behind Yoga Shastra and learn how personalised online yoga supports mothers, families, and everyday wellness.",
    path: "/about",
    keywords:
      "about yoga shastra, certified yoga instructors India, yogacharya online, prenatal yoga experts, online yoga studio India, yoga teachers for mothers",
    ogImage: OG_IMAGE,
  },

  gallery: {
    title: "Yoga Class Gallery | Yoga Shastra",
    description:
      "See real moments from Yoga Shastra's live online classes — prenatal sessions, postnatal recovery, and community wellness in action.",
    path: "/gallery",
    keywords:
      "yoga shastra gallery, online yoga class photos, prenatal yoga class images, postnatal yoga sessions, yoga community India",
    ogImage: OG_IMAGE,
  },

  blog: {
    title: "Yoga & Wellness Blog | Yoga Shastra",
    description:
      "Practical guidance on prenatal yoga, postnatal recovery, pranayama, and mindful living from the Yoga Shastra team.",
    path: "/blog",
    keywords:
      "yoga blog India, prenatal yoga tips, pregnancy yoga poses, postnatal recovery exercises, pranayama benefits, yoga for wellness, mindful living",
    ogImage: OG_IMAGE,
  },

  contact: {
    title: "Contact Yoga Shastra | Book a Class",
    description:
      "Have a question or ready to start? Reach the Yoga Shastra team to book a prenatal, postnatal, or wellness yoga class today.",
    path: "/contact",
    keywords:
      "contact yoga shastra, book online yoga class, yoga class enquiry, talk to a yogacharya, prenatal yoga booking, online yoga consultation",
    ogImage: OG_IMAGE,
  },

  prenatalQuery: {
    title: "Prenatal & Postnatal Yoga Enquiry | Yoga Shastra",
    description:
      "Have questions about prenatal or postnatal yoga? Send your query to a Yoga Shastra Yogacharya for guidance tailored to your stage of motherhood.",
    path: "/prenatal-enquiry",
    keywords:
      "prenatal yoga enquiry, postnatal yoga questions, is yoga safe during pregnancy, pregnancy yoga guidance, when to start yoga after delivery, ask a yogacharya",
    ogImage: OG_IMAGE,
  },

  diet: {
    title: "Yoga & Sattvic Diet Guidance | Yoga Shastra",
    description:
      "Learn how a sattvic diet paired with regular yoga practice supports physical and mental wellbeing, with guidance from Yoga Shastra.",
    path: "/diet",
    keywords:
      "sattvic diet, sattvic diet plan, yoga diet plan, yogic diet for beginners, yoga and nutrition, diet for yoga practitioners, sattvik ahar, ayurvedic diet for yoga",
    ogImage: OG_IMAGE,
  },

  yogaGame: {
    title: "Prana Runner — Yoga Game | Yoga Shastra",
    description:
      "Play Prana Runner, Yoga Shastra's free browser game. Hold space to inhale and rise, dodge stress, and collect lotus flowers along the way.",
    path: "/yoga-game",
    keywords: "prana runner, yoga game online, free yoga game, breathing exercise game, pranayama game, mindfulness game, fun yoga activity",
    ogImage: OG_IMAGE,
  },

  dietPlanner: {
    title: "Yoga Diet Planner | Yoga Shastra",
    description:
      "Plan meals that support your yoga practice with Yoga Shastra's diet planner — balanced, sattvic nutrition guidance for your goals and lifestyle.",
    path: "/dietPlanner",
    keywords:
      "yoga diet planner, sattvic meal plan, diet plan for yoga practitioners, healthy diet plan India, yoga and nutrition planner",
    ogImage: OG_IMAGE,
  },

  dietPlannerWizard: {
    title: "Personalised Diet Plan Wizard | Yoga Shastra",
    description:
      "Answer a few quick questions and get a personalised diet plan that complements your yoga routine, from the Yoga Shastra team.",
    path: "/diet-planner",
    keywords:
      "personalised diet plan, diet plan wizard, custom yoga diet plan, sattvic diet plan online, diet plan for wellness",
    ogImage: OG_IMAGE,
  },

  privacyPolicy: {
    title: "Privacy Policy | Yoga Shastra",
    description:
      "How Yoga Shastra collects, uses, and protects your personal data across our website and online classes.",
    path: "/privacy-policy",
    keywords: "yoga shastra privacy policy, data protection, website privacy",
    ogImage: OG_IMAGE,
  },

  cookiePolicy: {
    title: "Cookie Policy | Yoga Shastra",
    description:
      "How Yoga Shastra uses cookies to improve your browsing experience and personalise site content.",
    path: "/cookie-policy",
    keywords: "cookie policy, yoga shastra cookies, website cookies",
    ogImage: OG_IMAGE,
  },

  termsAndConditions: {
    title: "Terms & Conditions | Yoga Shastra",
    description:
      "The terms governing use of the Yoga Shastra website, online classes, and digital services.",
    path: "/terms-and-conditions",
    keywords:
      "yoga shastra terms and conditions, terms of service, online class agreement",
    ogImage: OG_IMAGE,
  },

  freeConsultation: {
    title: "Free Yoga Consultation | Yoga Shastra",
    description:
      "Book a free consultation with a Yoga Shastra Yogacharya and get personalised guidance on the right prenatal, postnatal, or wellness yoga plan for you.",
    path: "/freeConsultation",
    keywords:
      "free yoga consultation, free yoga demo class, free prenatal yoga trial class, book free yoga session online, personalised yoga plan, consult a yogacharya",
    ogImage: OG_IMAGE,
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// APP
// ═══════════════════════════════════════════════════════════════════════════════

function App() {
  // Track pageviews on initial load
  useEffect(() => {
    if (IS_PRODUCTION) {
      ReactGA.send({
        hitType: "pageview",
        page: window.location.pathname + window.location.search,
      });
    }
  }, []);

  return (
    <HelmetProvider>
      <>
        <GlobalStyles />

        {/* ── Site-wide structured data ── */}
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(organisationSchema)}
          </script>

          <script type="application/ld+json">
            {JSON.stringify(webSiteSchema)}
          </script>
        </Helmet>

        <ThemeProvider theme={light}>
          {/* Global route-change scroll handling */}
          <ScrollToTop />

          <Routes>
            {/* ── Homepage ─────────────────────────────────────────────────── */}
            <Route
              path="/"
              element={
                <PageLayout seo={<PageTitle {...SEO.home} />}>
                  <Home />
                  <Works />
                  <YogGoals />
                  <Need />
                  <About />
                  <Testimonials />
                </PageLayout>
              }
            />

            {/* Redirect legacy /home → / */}
            <Route
              path="/home"
              element={<Navigate to="/" replace />}
            />

            {/* ── Yoga for Health ──────────────────────────────────────────── */}
            <Route
              path="/yoga-for-health"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.yogaForHealth} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Yoga for Health" },
                  ]}
                >
                  <Rogyog
                    isUpeerWave={false}
                    isLowerWave={false}
                  />
                  <Need />
                </PageLayout>
              }
            />

            {/* Dynamic Yoga Goal routing */}
            <Route
              path="/yoga-goals/:goalId"
              element={
                <PageLayout>
                  <YogGoalAsanas />
                </PageLayout>
              }
            />

             <Route
              path="/yoga-goals"
              element={
                <PageLayout>
                  <YogGoalAsanas />
                </PageLayout>
              }
            />

            {/* ── Availability ─────────────────────────────────────────────── */}
            <Route
              path="/availability"
              element={
                <PageLayout
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Availability" },
                  ]}
                >
                  <Availability />
                </PageLayout>
              }
            />

            {/* ── Dynamic Yoga for Health routes ───────────────────────────── */}
            <Route
              path="/yoga-for-health/:category"
              element={
                <PageLayout>
                  <Rogyog
                    isUpeerWave={false}
                    isLowerWave={false}
                  />
                  <Need />
                </PageLayout>
              }
            />

            <Route
              path="/yoga-for-health/:category/:subcategory"
              element={
                <PageLayout>
                  <Rogyog
                    isUpeerWave={false}
                    isLowerWave={false}
                  />
                  <Need />
                </PageLayout>
              }
            />

            <Route
              path="/yoga-for-health/:category/:subcategory/:subsubcategory"
              element={
                <PageLayout>
                  <Rogyog
                    isUpeerWave={false}
                    isLowerWave={false}
                  />
                  <Need />
                </PageLayout>
              }
            />

            {/* Redirect legacy /rogyog paths */}
            <Route
              path="/rogyog"
              element={
                <Navigate
                  to="/yoga-for-health"
                  replace
                />
              }
            />

            <Route
              path="/rogyog/:cat"
              element={
                <Navigate
                  to="/yoga-for-health/:cat"
                  replace
                />
              }
            />

            <Route
              path="/rogyog/:cat/:sub"
              element={
                <Navigate
                  to="/yoga-for-health/:cat/:sub"
                  replace
                />
              }
            />

            <Route
              path="/rogyog/:cat/:sub/:subsub"
              element={
                <Navigate
                  to="/yoga-for-health/:cat/:sub/:subsub"
                  replace
                />
              }
            />

            {/* ── Online Yoga ──────────────────────────────────────────────── */}
            <Route
              path="/online-yoga"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.onlineYoga} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Yoga Practices" },
                  ]}
                >
                  <Yog
                    isUpeerWave={false}
                    isLowerWave={false}
                  />
                  <Works />
                </PageLayout>
              }
            />

            {/* Dynamic Online Yoga routes */}
            <Route
              path="/online-yoga/:category"
              element={
                <PageLayout>
                  <Yog />
                  <Works />
                </PageLayout>
              }
            />

            <Route
              path="/online-yoga/:category/:subcategory"
              element={
                <PageLayout>
                  <Yog />
                  <Works />
                </PageLayout>
              }
            />

            <Route
              path="/online-yoga/:category/:subcategory/:id"
              element={
                <PageLayout>
                  <Yog />
                  <Works />
                </PageLayout>
              }
            />

            <Route
              path="/online-yoga/:category/:subcategory/:subsubcategory/:id"
              element={
                <PageLayout>
                  <Yog />
                  <Works />
                </PageLayout>
              }
            />

            {/* Redirect legacy /yog paths */}
            <Route
              path="/yog"
              element={
                <Navigate
                  to="/online-yoga"
                  replace
                />
              }
            />

            <Route
              path="/yog/:cat"
              element={
                <Navigate
                  to="/online-yoga/:cat"
                  replace
                />
              }
            />

            <Route
              path="/yog/:cat/:sub"
              element={
                <Navigate
                  to="/online-yoga/:cat/:sub"
                  replace
                />
              }
            />

            <Route
              path="/yog/:cat/:sub/:id"
              element={
                <Navigate
                  to="/online-yoga/:cat/:sub/:id"
                  replace
                />
              }
            />

            {/* ── Diet & Wellness ──────────────────────────────────────────── */}
            <Route
              path="/diet"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.diet} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Diet & Wellness" },
                  ]}
                >
                  <Diet />
                  <Works />
                </PageLayout>
              }
            />

            {/* ── Packages ─────────────────────────────────────────────────── */}
            <Route
              path="/packages"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.packages} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Programs" },
                  ]}
                >
                  <Packages />
                </PageLayout>
              }
            />

            {/* ── Diet Planner ─────────────────────────────────────────────── */}
            <Route
              path="/dietPlanner"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.dietPlanner} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Diet Planner" },
                  ]}
                >
                  <DietPlanner />
                </PageLayout>
              }
            />

            {/* ── About ────────────────────────────────────────────────────── */}
            <Route
              path="/about"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.about} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "About" },
                  ]}
                >
                  <SliderComponent />
                  <Works />
                  <About />
                  <Need />
                </PageLayout>
              }
            />

            {/* ── Gallery ──────────────────────────────────────────────────── */}
            <Route
              path="/gallery"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.gallery} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Gallery" },
                  ]}
                >
                  <GalleryPage
                    isUpeerWave={false}
                    isLowerWave={false}
                  />
                </PageLayout>
              }
            />

            {/* ── Blog ─────────────────────────────────────────────────────── */}
            <Route
              path="/blog"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.blog} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Blog" },
                  ]}
                >
                  <Blog />
                  <Need />
                </PageLayout>
              }
            />

            {/* Blog post */}
            <Route
              path="/blog/:sN"
              element={
                <PageLayout>
                  <BlogDescription />
                </PageLayout>
              }
            />

            {/* ── Contact ──────────────────────────────────────────────────── */}
            <Route
              path="/contact"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.contact} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Contact" },
                  ]}
                >
                  <ContactUsForm />
                </PageLayout>
              }
            />

            {/* Redirect legacy /contactUs */}
            <Route
              path="/contactUs"
              element={
                <Navigate
                  to="/contact"
                  replace
                />
              }
            />

            {/* ── Prenatal Enquiry ─────────────────────────────────────────── */}
            <Route
              path="/prenatal-enquiry"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.prenatalQuery} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Prenatal Enquiry" },
                  ]}
                >
                  <ContactUSPrenatal />
                </PageLayout>
              }
            />

            {/* Redirect legacy /prenatalQuery */}
            <Route
              path="/prenatalQuery"
              element={
                <Navigate
                  to="/prenatal-enquiry"
                  replace
                />
              }
            />

            {/* ── Legal ────────────────────────────────────────────────────── */}
            <Route
              path="/privacy-policy"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.privacyPolicy} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Privacy Policy" },
                  ]}
                >
                  <PrivacyPolicy />
                </PageLayout>
              }
            />

            <Route
              path="/privacypolicy"
              element={
                <Navigate
                  to="/privacy-policy"
                  replace
                />
              }
            />

            <Route
              path="/cookie-policy"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.cookiePolicy} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Cookie Policy" },
                  ]}
                >
                  <CookiePolicy />
                </PageLayout>
              }
            />

            <Route
              path="/cookiepolicy"
              element={
                <Navigate
                  to="/cookie-policy"
                  replace
                />
              }
            />

            <Route
              path="/terms-and-conditions"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.termsAndConditions} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Terms & Conditions" },
                  ]}
                >
                  <TermsAndConditions />
                </PageLayout>
              }
            />

            {/* Redirect legacy /terms&condition */}
            <Route
              path="/terms&condition"
              element={
                <Navigate
                  to="/terms-and-conditions"
                  replace
                />
              }
            />

            {/* ── Welcome ──────────────────────────────────────────────────── */}
            <Route
              path="/diet-planner"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.dietPlannerWizard} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Diet Planner" },
                  ]}
                >
                  <DietPlannerWizard />
                </PageLayout>
              }
            />

            {/* ── Yoga Game ────────────────────────────────────────────────── */}
            <Route
              path="/yoga-game"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.yogaGame} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Yoga Game" },
                  ]}
                >
                  <YogaGame />
                </PageLayout>
              }
            />

            {/* ── Free Consultation ────────────────────────────────────────── */}
            <Route
              path="/freeConsultation"
              element={
                <PageLayout
                  seo={<PageTitle {...SEO.freeConsultation} />}
                  breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Free Consultation" },
                  ]}
                >
                  <AdvertisementForm />
                </PageLayout>
              }
            />

            {/* ── 404 Fallback ─────────────────────────────────────────────── */}
            <Route
              path="*"
              element={<Navigate to="/yoga-game" replace />}
            />
          </Routes>
        </ThemeProvider>
      </>
    </HelmetProvider>
  );
}

export default App;